<template>
    <div class="developer-settings">

        <!-- ACCESS TOKEN -->
        <div class="section">
            <div class="section-header">
                <div class="title">
                    <span class="material-icons">vpn_key</span>
                    {{ $t("settings.apiAccessTitle") }}
                </div>
                <div class="subtitle">{{ $t("settings.apiAccessSubtitles") }}</div>
                <div class="subtitle token-warning">{{ $t("developer.tokenGenerateWarning") }}</div>
            </div>

            <div class="token-box">
                <div class="token-value" :class="{ empty: !accessToken }">
                    <span v-if="accessToken">{{ truncatedToken }}</span>
                    <span v-else class="no-token">{{ $t("developer.noToken") }}</span>
                </div>
                <div class="token-actions">
                    <a-button icon="sync" :loading="tokenLoading" @click="generateToken">
                        {{ $t("message.generateToken") }}
                    </a-button>
                    <a-button icon="copy" :disabled="!accessToken" @click="copyToken">
                        {{ $t("message.copyToken") }}
                    </a-button>
                    <a-button icon="book" @click="goToApiDoc">
                        {{ $t("message.ApiDoc") }}
                    </a-button>
                </div>
            </div>
        </div>

        <!-- WEBHOOK URLs -->
        <div class="section">
            <div class="section-header">
                <div class="title">
                    <span class="material-icons">webhook</span>
                    {{ $t("developer.webhookUrls") }}
                </div>
                <div class="subtitle">{{ $t("developer.webhookUrlsDescription") }}</div>
            </div>

            <div class="webhook-list">
                <div class="webhook-item" v-for="(item, idx) in webhookUrls" :key="idx">
                    <a-input
                        v-model="item.name"
                        :placeholder="$t('developer.webhookNamePlaceholder')"
                        class="webhook-name-input"
                    />
                    <a-input
                        v-model="item.url"
                        :placeholder="$t('developer.webhookUrlPlaceholder')"
                        class="webhook-url-input"
                    />
                    <a-button type="danger" icon="delete" @click="removeWebhook(idx)" />
                </div>

                <a-button type="dashed" icon="plus" @click="addWebhook" class="add-webhook-btn">
                    {{ $t("developer.addWebhook") }}
                </a-button>
            </div>

            <a-button type="primary" :loading="saving" @click="save" class="save-btn">
                {{ $t("chatbotv2.save") }}
            </a-button>
        </div>

        <!-- API DOCS -->
        <div class="section">
            <div class="section-header">
                <div class="title">
                    <span class="material-icons">integration_instructions</span>
                    {{ $t("developer.messagingApi") }}
                </div>
                <div class="subtitle">{{ $t("developer.messagingApiDescription") }}</div>
            </div>

            <div class="api-docs">

                <!-- Mesaj Gönderme -->
                <div class="doc-group">
                    <div class="doc-group-title">
                        <span class="method-badge post">POST</span>
                        <span class="endpoint-path">{{ messagingEndpointPath }}</span>
                    </div>
                    <div class="doc-group-desc">{{ $t("developer.sendMessageDesc") }}</div>
                    <div class="doc-group-desc doc-params">{{ $t("developer.fromUserIdParamsDesc") }}</div>
                    <div class="doc-group-desc doc-params">
                        <span class="param-label">:contactId</span>
                        {{ $t("developer.contactIdParamsDesc") }}
                    </div>

                    <div class="doc-block">
                        <div class="doc-title">{{ $t("developer.authentication") }}</div>
                        <div class="code-block">
                            <div class="code-copy-row">
                                <pre class="language-bash"><code v-html="highlightBash('Authorization: Bearer ' + (accessToken ? truncatedToken : '&lt;access_token&gt;'))"></code></pre>
                                <a-button size="small" icon="copy" @click="copyAuthHeader" />
                            </div>
                        </div>
                    </div>

                    <div class="doc-block">
                        <div class="doc-title">{{ $t("developer.requestBody") }}</div>
                        <a-tabs>
                            <a-tab-pane key="text" tab="Text">
                                <div class="code-block"><pre class="language-json"><code v-html="highlightJson(textExample)"></code></pre></div>
                            </a-tab-pane>
                            <a-tab-pane key="image" tab="Image">
                                <div class="code-block"><pre class="language-json"><code v-html="highlightJson(imageExample)"></code></pre></div>
                            </a-tab-pane>
                            <a-tab-pane key="video" tab="Video">
                                <div class="code-block"><pre class="language-json"><code v-html="highlightJson(videoExample)"></code></pre></div>
                            </a-tab-pane>
                            <a-tab-pane key="document" tab="Document">
                                <div class="code-block"><pre class="language-json"><code v-html="highlightJson(documentExample)"></code></pre></div>
                            </a-tab-pane>
                        </a-tabs>
                    </div>

                    <div class="doc-block">
                        <div class="doc-title">{{ $t("developer.curlExample") }}</div>
                        <div class="code-block">
                            <div class="code-copy-row">
                                <pre class="language-bash"><code v-html="highlightBash(curlExampleDisplay)"></code></pre>
                                <a-button size="small" icon="copy" @click="copyCurl" />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Webhook -->
                <div class="doc-group">
                    <div class="doc-group-title">
                        <span class="method-badge post">POST</span>
                        <span class="endpoint-path">{{ $t("developer.yourWebhookUrl") }}</span>
                    </div>
                    <div class="doc-group-desc">{{ $t("developer.webhookPayloadDesc") }}</div>

                    <div class="doc-block">
                        <div class="doc-title">{{ $t("developer.webhookPayload") }}</div>
                        <a-tabs>
                            <a-tab-pane key="received" :tab="$t('developer.webhookEventReceived')">
                                <div class="code-block"><pre class="language-json"><code v-html="highlightJson(webhookPayloadExample)"></code></pre></div>
                            </a-tab-pane>
                            <a-tab-pane key="sent" :tab="$t('developer.webhookEventSent')">
                                <div class="doc-group-desc" style="margin-bottom: 8px;">{{ $t("developer.webhookSentDesc") }}</div>
                                <div class="code-block"><pre class="language-json"><code v-html="highlightJson(webhookSentPayloadExample)"></code></pre></div>
                            </a-tab-pane>
                        </a-tabs>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import _ from "lodash";
import { updateChannelWebhookUrls } from "../../../../api/channel";
import request from "../../../../utils/request";
import CONFIG from "../../../../config/config";
import { highlight, languages } from "prismjs/components/prism-core";
import "prismjs/components/prism-json";
import "prismjs/components/prism-bash";
import "prismjs/themes/prism-okaidia.css";

export default Vue.extend({
    name: "DeveloperSettings",
    props: {
        value: {
            required: true,
        },
    },
    data() {
        return {
            saving: false,
            tokenLoading: false,
            accessToken: null,
            webhookUrls: _.cloneDeep(_.get(this.value, "webhookUrls") || []),
            textExample: JSON.stringify(
                { text: { content: "Merhaba!" }, from: "<userObjectId>" },
                null,
                2
            ),
            imageExample: JSON.stringify(
                {
                    image: { url: "https://example.com/photo.jpg", caption: "Açıklama" },
                    from: "<userObjectId>",
                },
                null,
                2
            ),
            videoExample: JSON.stringify(
                { video: { url: "https://example.com/video.mp4" }, from: "<userObjectId>" },
                null,
                2
            ),
            documentExample: JSON.stringify(
                { document: { url: "https://example.com/file.pdf" }, from: "<userObjectId>" },
                null,
                2
            ),
            webhookPayloadExample: JSON.stringify({
                event: "message.received",
                channel: { id: "<channelId>", name: "Kanal Adı", type: "whatsapp-business-api-supsis" },
                visitor: { id: "<visitorId>", fullname: "Müşteri Adı", phone: "+905551234567" },
                message: {
                    id: "<uniqMessageId>",
                    type: "visitor",
                    text: "Mesaj içeriği",
                    asset: null,
                    conversationId: "<conversationId>",
                    createdAt: "2026-01-01T00:00:00.000Z",
                },
            }, null, 2),
            webhookSentPayloadExample: JSON.stringify({
                event: "message.sent",
                status: "sent",
                channel: { id: "<channelId>", name: "Kanal Adı", type: "whatsapp-business-api-supsis" },
                visitor: { id: "<visitorId>", fullname: "Müşteri Adı", phone: "+905551234567" },
                message: {
                    id: "<uniqMessageId>",
                    type: "user",
                    text: "Mesaj içeriği",
                    asset: null,
                    conversationId: "<conversationId>",
                    externalProviderMessageId: null,
                    createdAt: "2026-01-01T00:00:00.000Z",
                },
            }, null, 2),
        };
    },
    computed: {
        truncatedToken() {
            if (!this.accessToken || !this.accessToken.token) return "";
            var token = this.accessToken.token;
            if (token.length <= 40) return token;
            return token.substring(0, 40) + "...";
        },
        messagingEndpointPath() {
            const baseUrl = CONFIG.SOCKET_BASE_URL || "https://socket.supsis.live";
            return baseUrl + "/api/customer/v1/contacts/:contactId/messages";
        },
        curlExample() {
            const token = this.accessToken ? this.accessToken.token : "<access_token>";
            const baseUrl = CONFIG.SOCKET_BASE_URL || "https://socket.supsis.live";
            return `curl -X POST ${baseUrl}/api/customer/v1/contacts/<contactId>/messages \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{"text": {"content": "Merhaba!"}, "from": "<userObjectId>"}'`;
        },
        curlExampleDisplay() {
            const token = this.accessToken ? this.truncatedToken : "<access_token>";
            const baseUrl = CONFIG.SOCKET_BASE_URL || "https://socket.supsis.live";
            return `curl -X POST ${baseUrl}/api/customer/v1/contacts/<contactId>/messages \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{"text": {"content": "Merhaba!"}, "from": "<userObjectId>"}'`;
        },
    },
    methods: {
        async getToken() {
            try {
                const { data } = await request.get("/access-token");
                this.accessToken = data.access_token;
            } catch (e) {}
        },
        async generateToken() {
            this.tokenLoading = true;
            try {
                const { data } = await request.post("/access-token/generate-token");
                this.accessToken = data.access_token;
                this.$message.success(this.$t("developer.tokenGenerated"));
            } catch (e) {
                this.$message.error(this.$t("developer.tokenGenerateError"));
            } finally {
                this.tokenLoading = false;
            }
        },
        copyToken() {
            this.copyToClipboard(this.accessToken.token);
            this.$message.success(this.$t("message.tokenCopied"));
        },
        copyAuthHeader() {
            const token = this.accessToken ? this.accessToken.token : "<access_token>";
            this.copyToClipboard("Authorization: Bearer " + token);
            this.$message.success(this.$t("developer.copied"));
        },
        copyCurl() {
            this.copyToClipboard(this.curlExample);
            this.$message.success(this.$t("developer.copied"));
        },
        copyToClipboard(text) {
            const el = document.createElement("textarea");
            el.value = text;
            document.body.appendChild(el);
            el.select();
            document.execCommand("copy");
            document.body.removeChild(el);
        },
        highlightJson(code) {
            try {
                return highlight(code, languages.json, "json");
            } catch (e) {
                return code;
            }
        },
        highlightBash(code) {
            try {
                return highlight(code, languages.bash, "bash");
            } catch (e) {
                return code;
            }
        },
        goToApiDoc() {
            window.open("https://doc.supsis.live/api/", "_blank");
        },
        addWebhook() {
            this.webhookUrls.push({ name: "", url: "" });
        },
        removeWebhook(idx) {
            this.webhookUrls.splice(idx, 1);
        },
        async save() {
            const channelId = this.value._id;
            if (!channelId) return;
            const valid = this.webhookUrls.every((w) => w.name && w.url);
            if (!valid) {
                this.$message.warning(this.$t("developer.webhookValidationError"));
                return;
            }
            this.saving = true;
            try {
                await updateChannelWebhookUrls(channelId, this.webhookUrls);
                this.$message.success(this.$t("developer.webhookSaved"));
                const updated = _.cloneDeep(this.value);
                updated.webhookUrls = _.cloneDeep(this.webhookUrls);
                this.$emit("input", updated);
            } catch (err) {
                console.error(err);
                this.$message.error(this.$t("developer.webhookSaveError"));
            } finally {
                this.saving = false;
            }
        },
    },
    watch: {
        value: {
            handler(val) {
                this.webhookUrls = _.cloneDeep(_.get(val, "webhookUrls") || []);
            },
            deep: true,
        },
    },
    mounted() {
        this.getToken();
    },
});
</script>

<style scoped lang="scss">
.developer-settings {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 32px;
    max-width: 900px;

    .section {
        display: flex;
        flex-direction: column;
        gap: 16px;

        .section-header {
            .title {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 16px;
                font-weight: 700;
                color: var(--color-text-strong-default);

                .material-icons {
                    font-size: 20px;
                    color: var(--color-bg-primary-default);
                }
            }

            .subtitle {
                font-size: 13px;
                color: var(--color-text-default);
                margin-top: 4px;
            }

            .token-warning {
                color: var(--color-text-default);
                font-style: italic;
            }
        }

        .token-box {
            display: flex;
            flex-direction: column;
            gap: 12px;
            background: var(--color-bg-neutral-low);
            border: 1px solid var(--color-border-default);
            border-radius: 10px;
            padding: 16px;

            .token-value {
                font-family: "Courier New", monospace;
                font-size: 13px;
                color: var(--color-text-strong-default);
                word-break: break-all;
                min-height: 24px;

                .no-token {
                    color: var(--color-text-default);
                    font-style: italic;
                    font-family: inherit;
                }
            }

            .token-actions {
                display: flex;
                gap: 8px;
                flex-wrap: wrap;
            }
        }

        .webhook-list {
            display: flex;
            flex-direction: column;
            gap: 10px;

            .webhook-item {
                display: flex;
                gap: 8px;
                align-items: center;

                .webhook-name-input {
                    width: 200px;
                    min-width: 200px;
                }

                .webhook-url-input {
                    flex: 1;
                }
            }

            .add-webhook-btn {
                width: fit-content;
            }
        }

        .save-btn {
            width: fit-content;
        }

        .api-docs {
            display: flex;
            flex-direction: column;
            gap: 24px;

            .doc-group {
                border: 1px solid var(--color-border-default);
                border-radius: 10px;
                overflow: hidden;

                .doc-group-title {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 12px 16px;
                    background: var(--color-bg-neutral-low);
                    border-bottom: 1px solid var(--color-border-default);

                    .method-badge {
                        font-size: 11px;
                        font-weight: 700;
                        padding: 2px 8px;
                        border-radius: 4px;
                        text-transform: uppercase;

                        &.post {
                            background: #d4edda;
                            color: #155724;
                        }
                    }

                    .endpoint-path {
                        font-family: "Courier New", monospace;
                        font-size: 13px;
                        font-weight: 600;
                        color: var(--color-text-strong-default);
                    }
                }

                .doc-group-desc {
                    padding: 10px 16px;
                    font-size: 13px;
                    color: var(--color-text-default);
                    border-bottom: 1px solid var(--color-border-default);

                    .param-label {
                        font-family: "Courier New", monospace;
                        font-size: 12px;
                        font-weight: 600;
                        background: rgba(255, 107, 53, 0.12);
                        color: #e65c2e;
                        border-radius: 3px;
                        padding: 1px 5px;
                        margin-right: 4px;
                    }
                }

                .doc-block {
                    padding: 14px 16px;

                    & + .doc-block {
                        border-top: 1px solid var(--color-border-default);
                    }

                    .doc-title {
                        font-size: 12px;
                        font-weight: 600;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        color: var(--color-text-default);
                        margin-bottom: 8px;
                    }

                    .code-block {
                        border: 1px solid var(--color-border-default);
                        border-radius: 6px;
                        overflow: hidden;
                        font-family: "Courier New", monospace;
                        font-size: 12px;

                        .code-copy-row {
                            display: flex;
                            align-items: flex-start;
                            justify-content: space-between;
                            gap: 10px;

                            pre {
                                margin: 0;
                                flex: 1;
                                min-width: 0;
                            }
                        }

                        pre {
                            margin: 0;
                            padding: 12px 14px;
                            white-space: pre-wrap;
                            word-break: break-all;
                            overflow-x: auto;
                        }

                        pre code {
                            font-family: inherit;
                        }
                    }
                }
            }
        }
    }
}
</style>

